import React, { useEffect, useState, useMemo } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Logout03Icon } from '@hugeicons/core-free-icons';
import { confirm } from '@/components/confirm-dialog';
import { CountBadge } from '@/components/count-badge';
import NavMenu from '../../components/app/NavMenu';
import Icon from '../../utils/components/Icon';
import logoutHandler from '../../utils/controllers/logoutHandler';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useGetWindowWidth from '../../utils/controllers/useGetWindowWidth';
import {
  ALWAYS_ALLOWED_PATH_PREFIXES,
  EVENTS_ALLOWED_PATH_PREFIXES,
  buildBottomNavItems,
  buildPrimaryNavItems,
} from '../../constants/navigation/navConfig';
import { useSidebarMenuContext } from './SidebarMenuContext';
import { selectIsEventsPlatform, selectPlatform } from '../../redux/slices/platform/platformSlice';

const MOBILE_BREAKPOINT = 768;

const getIcon = name => (
  <Icon name={name} color="primary" className="size-[18px]! text-[18px]! [color:inherit]!" />
);

/**
 * Returns true if the current URL is already covered by the filtered sidebar tree
 * (including sub-routes like /agents/edit when /agents/dashboard is in the menu).
 */
function pathMatchesFilteredNav(pathname, items, extraAllowedPrefixes = []) {
  const allowedPrefixes = [...ALWAYS_ALLOWED_PATH_PREFIXES, ...extraAllowedPrefixes];
  if (allowedPrefixes.some(p => pathname === p || pathname.startsWith(`${p}/`))) {
    return true;
  }

  const registerPath = path => {
    if (!path) return;
    if (path === '/') {
      exact.add('/');
      return;
    }
    exact.add(path);
    const parts = path.split('/').filter(Boolean);
    if (parts.length > 0) {
      bases.add(`/${parts[0]}`);
    }
  };

  const exact = new Set();
  const bases = new Set();

  items.forEach(item => {
    registerPath(item.path);
    item.children?.forEach(child => registerPath(child.path));
  });

  if (pathname === '/') {
    return exact.has('/');
  }
  if (exact.has(pathname)) {
    return true;
  }
  for (const base of bases) {
    if (base !== '/' && pathname.startsWith(`${base}/`)) {
      return true;
    }
  }
  return false;
}

const SidebarMenu = ({ secondary, inlineCollapsed = false }) => {
  const width = useGetWindowWidth();
  const isMobile = width <= MOBILE_BREAKPOINT;
  const { accessibleSection, isAccessibleSectionLoaded } = useSelector(state => state.team.data);
  const hasCreditAccess = useSelector(state => state.app.hasCreditAccess);
  const isSponsorsActive = useSelector(state => state.branding.branding.isSponsorsActive);
  const isEventsPlatform = useSelector(selectIsEventsPlatform);
  const platform = useSelector(selectPlatform);
  const [filteredNavItems, setFilteredNavItems] = useState([]);
  const { openKeys, setOpenKeys } = useSidebarMenuContext();
  const [showVideosNotification, setShowVideosNotification] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const videosVisited = localStorage.getItem('videosVisited');
    setShowVideosNotification(!videosVisited);
  }, []);

  const markVideosAsVisited = () => {
    localStorage.setItem('videosVisited', 'true');
    setShowVideosNotification(false);
  };

  const MenuBadge = ({ label, showDot, color }) => {
    return (
      <CountBadge
        dot={showDot}
        offset={[20, 15]}
        indicatorClassName="size-3 min-w-3 animate-pulse border-2 border-sidebar"
        indicatorStyle={color ? { backgroundColor: color } : undefined}
      >
        <span>{label}</span>
      </CountBadge>
    );
  };

  const handleClick = path => {
    if (path === '/videos') {
      markVideosAsVisited();
    }
    navigate(path);
  };

  const navItems = useMemo(
    () => buildPrimaryNavItems(handleClick, getIcon, platform),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [platform]
  );

  const bottomNavItems = useMemo(() => {
    const items = buildBottomNavItems({
      handleClick,
      getIcon,
      hasCreditAccess,
      isSponsorsActive,
      showVideosNotification,
      MenuBadge,
      platform,
    });

    if (isAccessibleSectionLoaded && accessibleSection?.length > 0) {
      return items.filter(item => {
        if (!item.sectionname) return true;
        return accessibleSection.includes(item.sectionname);
      });
    }

    return items;
  }, [
    hasCreditAccess,
    isSponsorsActive,
    showVideosNotification,
    accessibleSection,
    isAccessibleSectionLoaded,
    platform,
  ]);

  const handleOpenChange = keys => {
    const latestOpenKey = keys.find(key => openKeys.indexOf(key) === -1);

    if (latestOpenKey) {
      setOpenKeys([latestOpenKey]);
      const shouldAutoNavigateOnOpen = !isMobile && !inlineCollapsed;

      if (shouldAutoNavigateOnOpen) {
        const parentItem =
          navItems.find(item => item.key === latestOpenKey) ||
          bottomNavItems.find(item => item.key === latestOpenKey);

        if (parentItem?.children?.length > 0) {
          const firstChild = parentItem.children[0];
          if (firstChild.path) {
            navigate(firstChild.path);
          }
        }
      }
    } else {
      setOpenKeys([]);
    }
  };

  // Collapsed icon-rail shouldn't keep inline submenus open
  useEffect(() => {
    if (inlineCollapsed) {
      setOpenKeys([]);
    }
  }, [inlineCollapsed, setOpenKeys]);

  // Expanded: open the section that holds the current page (on load, after expanding the
  // rail, or when arriving from the command palette), so the active page is always visible.
  const activeKey = location.pathname.split('/')[1];
  useEffect(() => {
    if (inlineCollapsed) return;
    const items = secondary ? bottomNavItems : navItems;
    const activeParent = items.find(item => item.children?.some(child => child.key === activeKey));
    if (activeParent) {
      setOpenKeys(keys => (keys.includes(activeParent.key) ? keys : [activeParent.key]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, inlineCollapsed, secondary]);

  const menuComp = items => (
    <NavMenu
      inlineCollapsed={inlineCollapsed}
      selectedKeys={[location.pathname.split('/')[1]]}
      openKeys={openKeys}
      onOpenChange={handleOpenChange}
      items={items}
      style={{
        border: 'none',
        width: '100%',
      }}
      onClick={e => {
        // Top-level items without children don't trigger onOpenChange,
        // so collapse any open submenu when one of them is selected.
        if (e.keyPath?.length === 1) {
          setOpenKeys([]);
        }

        if (e.key === 'logout') {
          confirm({
            title: 'Log out?',
            description: "You'll need to sign in again to get back to your dashboard.",
            icon: <HugeiconsIcon icon={Logout03Icon} strokeWidth={2} />,
            onConfirm: () => {
              logoutHandler();
            },
            confirmText: 'Log out',
            cancelText: 'Cancel',
          });
        }
      }}
    />
  );

  useEffect(() => {
    if (isAccessibleSectionLoaded && accessibleSection) {
      let items;
      if (accessibleSection?.length === 0) {
        items = navItems;
      } else {
        items = navItems.filter(item => accessibleSection?.includes(item.sectionname));

        if (items?.length > 0) {
          const firstItem = items[0];
          const children = firstItem?.children;
          const parentPath = firstItem?.path;
          let firstChildPath = '';

          if (children?.length > 0) {
            firstChildPath = children[0]?.path;
          }

          const targetPath = firstChildPath || parentPath;
          const extraAllowedPrefixes = isEventsPlatform ? EVENTS_ALLOWED_PATH_PREFIXES : [];
          const shouldRedirect =
            targetPath &&
            !pathMatchesFilteredNav(
              location.pathname,
              [...items, ...bottomNavItems],
              extraAllowedPrefixes
            );

          if (shouldRedirect) {
            navigate(targetPath);
          }
        }
      }
      setFilteredNavItems(items);
    }
  }, [
    accessibleSection,
    isAccessibleSectionLoaded,
    location.pathname,
    navigate,
    bottomNavItems,
    navItems,
    isEventsPlatform,
  ]);

  return (
    <>
      {!secondary && isAccessibleSectionLoaded && menuComp(filteredNavItems)}
      {secondary && menuComp(bottomNavItems)}
    </>
  );
};

export default SidebarMenu;
